import {
  useGetQuery,
  useLazyGetQuery,
  usePostMutation,
  usePutMutation,
} from "slices/api";
import { METER_READING } from "tagTypes";

export const useGetMeterReadingQuery = (billCycleId) =>
  useGetQuery({
    endpoint: `/billing-cycle/${billCycleId}/meter-reading`,
    tags: [METER_READING],
  });

export const useGetMeterReadingDetailsQuery = (applicationId) =>
  useGetQuery({
    endpoint: `/other-utility-application/${applicationId}/other-utility-meter-reading-detail`,
    tags: [METER_READING],
  });

export const useGetMeterReadingDataByMeterIdQuery = (
  billCycleId,
  utilityConsumerId
) =>
  useGetQuery({
    endpoint: `/billing-cycle/${billCycleId}/utility-consumer/${utilityConsumerId}/meter-reading`,
    tags: [METER_READING],
  });

export const useGetFpppaChargesByBillCycleId = (billCycleId) =>
  useGetQuery({
    endpoint: `billing-cycle/${billCycleId}/fpppa-charges`,
    tags: [METER_READING],
  });

export const useGetOtherUtilityMeterReadingDataByMeterIdQuery = (
  billCycleId,
  utilityConsumerId
) =>
  useGetQuery({
    endpoint: `/billing-cycle/${billCycleId}/utility-consumer/${utilityConsumerId}/other-utility-meter-reading`,
    tags: [METER_READING],
  });

export const useLazyMeterReadingQuery = () => {
  const [getMeterReadings, data] = useLazyGetQuery();
  const meterReadings = async (billCycleId, query = {}) =>
    await getMeterReadings(
      {
        endpoint: `/billing-cycle/${billCycleId}/meter-reading`,
        query,
        tags: [METER_READING],
      },
      true
    ).unwrap();
  return [meterReadings, data];
};

export const useLazyGetFpppaChargesByBillCycleId = () => {
  const [getFPPPACharges, data] = useLazyGetQuery();
  const fpppaCharges = async (billCycleId, query = {}) =>
    await getFPPPACharges(
      {
        endpoint: `billing-cycle/${billCycleId}/fpppa-charges`,
        query,
        tags: [METER_READING],
      },
      true
    ).unwrap();
  return [fpppaCharges, data];
};

export const useLazyDailyMeterReadingsQuery = () => {
  const [getMeterReadings, data] = useLazyGetQuery();
  const meterReadings = async (billCycleId, query = {}) =>
    await getMeterReadings(
      {
        endpoint: `/report/billing-cycle/${billCycleId}/daily-meter-reading`,
        query,
        tags: [METER_READING],
      },
      true
    ).unwrap();
  return [meterReadings, data];
};

export const useLazyGetOtherUtilityMeterReadingQuery = () => {
  const [getMeterReadings, data] = useLazyGetQuery();
  const meterReadings = async (billCycleId, query = {}) =>
    await getMeterReadings(
      {
        endpoint: `/billing-cycle/${billCycleId}/other-utility-meter-reading`,
        query,
        tags: [METER_READING],
      },
      true
    ).unwrap();
  return [meterReadings, data];
};

export const usePostUpdatedMeterReadingsMutation = () => {
  const [setMeterReading, data] = usePostMutation();
  const addMeterReading = async (billingCycleId, payload) =>
    await setMeterReading({
      endpoint: `/billing-cycle/${billingCycleId}/meter-reading/bill-calculation`,
      payload: payload || {},
    }).unwrap();
  return [addMeterReading, data];
};

export const usePostOtherUtilityUpdatedMeterReadingsMutation = () => {
  const [setMeterReading, data] = usePostMutation();
  const addMeterReading = async (billingCycleId, payload) =>
    await setMeterReading({
      endpoint: `/billing-cycle/${billingCycleId}/other-utility-meter-reading/bill-calculation`,
      payload: payload || {},
    }).unwrap();
  return [addMeterReading, data];
};

export const usePutMeterReadingMutation = () => {
  const [updateMeterReading, data] = usePutMutation();
  const updateMeterReadingData = async (billCycleId, payload) =>
    await updateMeterReading({
      endpoint: `billing-cycle/${billCycleId}/meter-reading`,
      payload: payload || {},
      toastMessage: "Meter reading updated successfully",
      tags: [METER_READING],
    }).unwrap();
  return [updateMeterReadingData, data];
};

export const usePutOtherUtilityMeterReadingMutation = () => {
  const [updateMeterReading, data] = usePutMutation();
  const updateMeterReadingData = async (billCycleId, payload) =>
    await updateMeterReading({
      endpoint: `/billing-cycle/${billCycleId}/other-utility-meter-reading`,
      payload: payload || {},
      toastMessage: "Meter reading updated successfully",
      tags: [METER_READING],
    }).unwrap();
  return [updateMeterReadingData, data];
};

export const usePostUploadMeterReadingsFileMutation = () => {
  const [uploadMeterReadingsFile, data] = usePostMutation();
  const addMeterReadingsFile = async (payload) =>
    await uploadMeterReadingsFile({
      endpoint: "meter-reading-upload",
      tags: [METER_READING],
      payload: payload || {},
      toastMessage: "Meter readings uploaded successfully",
    }).unwrap();
  return [addMeterReadingsFile, data];
};

export const usePostOtherUtilityUploadMeterReadingsFileMutation = () => {
  const [uploadMeterReadingsFile, data] = usePostMutation();
  const addMeterReadingsFile = async (payload, query = {}) =>
    await uploadMeterReadingsFile({
      endpoint: "/other-utility-meter-reading-upload",
      tags: [METER_READING],
      query,
      payload: payload || {},
      toastMessage: "Meter readings uploaded successfully",
    }).unwrap();
  return [addMeterReadingsFile, data];
};

export const usePostUploadBillAdjustmentFileMutation = () => {
  const [uploadBillAdjustmentFile, data] = usePostMutation();
  const addBillingAdjustmentFile = async (billingCycleId, payload) =>
    await uploadBillAdjustmentFile({
      endpoint: `/billing-cycle/${billingCycleId}/bill-adjustment-upload`,
      tags: [METER_READING],
      payload: payload || {},
    }).unwrap();
  return [addBillingAdjustmentFile, data];
};

export const usePostOtherUtilityUploadBillAdjustmentFileMutation = () => {
  const [uploadBillAdjustmentFile, data] = usePostMutation();
  const addBillingAdjustmentFile = async (billingCycleId, payload) =>
    await uploadBillAdjustmentFile({
      endpoint: `/billing-cycle/${billingCycleId}/bill-adjustment-upload`,
      tags: [METER_READING],
      payload: payload || {},
    }).unwrap();
  return [addBillingAdjustmentFile, data];
};
